import { CircleDot } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { BookingState } from "@/types/booking";
import { BOOKING_STATE_LABELS } from "@/types/booking";

import type { KaiEntityTag } from "./kaiEntityTags";

/** Filter token(s) sent to booking / metrics tools (`state` or `state_not`). */
export type KaiBookingStateFilter =
  | { mode: "state"; value: string }
  | { mode: "state_not"; value: string };

export interface KaiBookingStateTagDef {
  /** Stable id for search / dedupe (not always equal to filter value). */
  id: string;
  label: string;
  filter: KaiBookingStateFilter;
  /** Short freight-ops hint shown in the `@` menu — not DB jargon. */
  opsHint: string;
  /** Extra keywords for `@` search (English + common ops phrases). */
  keywords?: string[];
}

const STATE_DEFS: KaiBookingStateTagDef[] = [
  {
    id: "pending",
    label: BOOKING_STATE_LABELS.pending,
    filter: { mode: "state", value: "pending" },
    opsHint: "Job exists in the system; carrier booking is not finalized yet.",
  },
  {
    id: "pending_book",
    label: BOOKING_STATE_LABELS.pending_book,
    filter: { mode: "state", value: "pending_book" },
    opsHint:
      "Waiting on booking details / confirmation from the carrier or agent (Pending Book queue).",
    keywords: ["pending book", "booking details", "בוקינג"],
  },
  {
    id: "book",
    label: BOOKING_STATE_LABELS.book,
    filter: { mode: "state", value: "book" },
    opsHint: "Confirmed with the carrier — sailing plan is set, pre-departure.",
    keywords: ["booked", "confirmed"],
  },
  {
    id: "book_restricted",
    label: BOOKING_STATE_LABELS.book_restricted,
    filter: { mode: "state", value: "book_restricted" },
    opsHint:
      "Booked, but flagged with a restriction (allocation, policy, or commercial hold).",
    keywords: ["restricted"],
  },
  {
    id: "rolled",
    label: BOOKING_STATE_LABELS.rolled,
    filter: { mode: "state", value: "rolled" },
    opsHint: "Vessel or sailing rolled — departure moved to a later schedule.",
    keywords: ["roll", "rolled", "delay"],
  },
  {
    id: "stuffed",
    label: BOOKING_STATE_LABELS.stuffed,
    filter: { mode: "state", value: "stuffed" },
    opsHint: "Container gated in / stuffed — ready for export on the booked vessel.",
    keywords: ["gate in", "gated", "stuffed"],
  },
  {
    id: "shipped",
    label: BOOKING_STATE_LABELS.shipped,
    filter: { mode: "state", value: "shipped" },
    opsHint: "On the water (or handed over to carrier) — in transit to destination.",
    keywords: ["in transit", "sailed", "on board"],
  },
  {
    id: "delivered",
    label: BOOKING_STATE_LABELS.delivered,
    filter: { mode: "state", value: "delivered" },
    opsHint: "Arrived and closed out at destination — lifecycle complete.",
    keywords: ["arrived", "completed"],
  },
  {
    id: "cancelled",
    label: BOOKING_STATE_LABELS.cancelled,
    filter: { mode: "state", value: "cancelled" },
    opsHint: "Cancelled — no longer moving on the original plan.",
  },
  {
    id: "rebooked",
    label: BOOKING_STATE_LABELS.rebooked,
    filter: { mode: "state", value: "rebooked" },
    opsHint: "Moved to a different carrier contract / sailing via rebook.",
    keywords: ["rebook", "re-booked"],
  },
];

/** Common multi-state scopes operators ask for in plain language. */
const SCOPE_DEFS: KaiBookingStateTagDef[] = [
  {
    id: "scope_open",
    label: "Open jobs (Pending + Pending book)",
    filter: { mode: "state", value: "pending,pending_book" },
    opsHint:
      "Same as “open bookings” — the pre-book backlog (not the same as Active).",
    keywords: ["open bookings", "open jobs"],
  },
  {
    id: "scope_active",
    label: "Active pipeline",
    filter: { mode: "state_not", value: "delivered,cancelled" },
    opsHint:
      "Everything still in play — excludes only delivered and cancelled.",
    keywords: ["active", "in flight", "live jobs"],
  },
  {
    id: "scope_booked_only",
    label: "Booked pipeline only",
    filter: {
      mode: "state",
      value: "book,book_restricted,rolled,stuffed,shipped",
    },
    opsHint:
      "Confirmed and onward — excludes open/pending backlog and closed deliveries.",
    keywords: ["booked only", "confirmed only"],
  },
];

export const KAI_BOOKING_STATE_TAG_DEFS: KaiBookingStateTagDef[] = [
  ...STATE_DEFS,
  ...SCOPE_DEFS,
];

export const KAI_BOOKING_STATE_ICON: LucideIcon = CircleDot;

function defToTag(def: KaiBookingStateTagDef): KaiEntityTag {
  return {
    kind: "booking_state",
    value: def.filter.value,
    label: def.label,
    metadata: {
      state_filter_mode: def.filter.mode,
      ops_hint: def.opsHint,
      booking_state_id: def.id,
    },
  };
}

/** Local search for `@` → Booking state (no API). */
export function searchBookingStateTags(query: string): KaiEntityTag[] {
  const q = query.trim().toLowerCase();
  const defs = !q
    ? KAI_BOOKING_STATE_TAG_DEFS
    : KAI_BOOKING_STATE_TAG_DEFS.filter((def) => {
        if (def.label.toLowerCase().includes(q)) return true;
        if (def.id.includes(q)) return true;
        if (def.filter.value.includes(q)) return true;
        return def.keywords?.some((k) => k.toLowerCase().includes(q)) ?? false;
      });
  const seen = new Set<string>();
  const out: KaiEntityTag[] = [];
  for (const def of defs) {
    const key = `${def.filter.mode}:${def.filter.value}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(defToTag(def));
    if (out.length >= 10) break;
  }
  return out;
}

/** Resolve a single canonical API state string to a tag when possible. */
export function bookingStateTagFromApiState(state: BookingState): KaiEntityTag | null {
  const def = STATE_DEFS.find((d) => d.id === state);
  return def ? defToTag(def) : null;
}
